/**
 * useLivePrices — sub-second price feed via WebSocket
 *
 * Connects once (shared socket) to the indexer's /ws/prices endpoint, which
 * re-broadcasts Binance miniTicker updates.  All component instances share the
 * same connection; updates cause only affected components to re-render.
 *
 * Usage:
 *   const prices = useLivePrices()          // { BTC: 42350, ETH: 2300, ... }
 *   const btcUsd = useLivePrice('BTC')       // 42350 | undefined
 *   const live   = useLivePriceByAddress(tokenAddress)
 */
import { useEffect, useState } from "react"
import { INDEXER_BASE } from "./client"

// Convert http(s):// → ws(s)://, or use current page origin for Vite proxy mode
const WS_BASE = INDEXER_BASE.startsWith("http")
    ? INDEXER_BASE.replace(/^http/, "ws")
    : (typeof window !== "undefined" ? window.location.origin.replace(/^http/, "ws") : "ws://localhost:3010")
const WS_URL  = `${WS_BASE}/ws/prices`

export type PriceMap = Record<string, number>

// ── Shared singleton state ────────────────────────────────────────────────────

let socket:      WebSocket | null = null
let priceCache:  PriceMap = {}
const listeners: Set<(prices: PriceMap) => void> = new Set()
let reconnectTimer: ReturnType<typeof setTimeout> | null = null

function broadcast() {
    listeners.forEach(fn => fn(priceCache))
}

function connect() {
    if (socket && socket.readyState !== WebSocket.CLOSED) return

    socket = new WebSocket(WS_URL)

    socket.onmessage = (event) => {
        try {
            const msg: { type: string; data: any } = JSON.parse(event.data)
            if (msg.type === "snapshot") {
                priceCache = { ...msg.data }
                broadcast()
            } else if (msg.type === "tick") {
                const { symbol, price } = msg.data as { symbol: string; price: number; ts: number }
                priceCache = { ...priceCache, [symbol]: price }
                broadcast()
            }
        } catch (_) { /* ignore malformed frames */ }
    }

    socket.onclose = () => {
        socket = null
        if (listeners.size > 0) {
            reconnectTimer = setTimeout(connect, 3000)
        }
    }

    socket.onerror = () => { /* close handler will reconnect */ }
}

export function subscribe(fn: (prices: PriceMap) => void) {
    listeners.add(fn)
    if (listeners.size === 1) {
        // First subscriber — open the connection
        if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null }
        connect()
    }
    return () => {
        listeners.delete(fn)
        if (listeners.size === 0 && socket) {
            // Last subscriber gone — close cleanly.
            // Don't close while CONNECTING: browsers throw "closed before connection
            // established" (a StrictMode double-invoke artifact in dev).
            if (socket.readyState !== WebSocket.CONNECTING) {
                socket.close()
            }
            socket = null
        }
    }
}

// ── Hooks ─────────────────────────────────────────────────────────────────────

/** Returns a map of all live prices, updated on every tick. */
export function useLivePrices(): PriceMap {
    const [prices, setPrices] = useState<PriceMap>(() => ({ ...priceCache }))

    useEffect(() => {
        // Sync with current cache immediately (may have been seeded by another hook)
        setPrices({ ...priceCache })
        return subscribe((p) => setPrices({ ...p }))
    }, [])

    return prices
}

/** Returns the live price (USD) for a given symbol, e.g. 'BTC', 'ETH'. */
export function useLivePrice(symbol: string): number | undefined {
    const prices = useLivePrices()
    return prices[symbol.toUpperCase()]
}

/** Returns the live price (USD) for a token by contract address. */
export function useLivePriceByAddress(address: string, chainId = 56): number | undefined {
    const prices = useLivePrices()
    const addrMap = ADDRESS_TO_SYMBOL_BY_CHAIN[chainId] ?? ADDRESS_TO_SYMBOL_BY_CHAIN[56]
    const sym = addrMap[address.toLowerCase()]
    return sym ? prices[sym] : undefined
}

/**
 * Sync accessor — returns the cached price without subscribing.
 * Safe to call outside React (e.g. in order submission logic).
 */
export function getLivePrice(symbol: string): number | undefined {
    return priceCache[symbol.toUpperCase()]
}

export function getLivePriceSnapshot(): PriceMap {
    return { ...priceCache }
}

// ── Index-prices format (address → bigint in 1e30 units) ──────────────────────
// Used by useInfoTokens to override on-chain Chainlink prices with live data.

// token address (lowercase) → canonical price symbol, keyed by chainId
const ADDRESS_TO_SYMBOL_BY_CHAIN: Record<number, Record<string, string>> = {
    56: { // BSC
        "0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c": "BNB",
        "0x2170ed0880ac9a755fd29b2688956bd959f933f8": "ETH",
        "0x7130d2a12b9bcbfae4f2634d864a1ee1ce3ead9c": "BTC",
        "0xba2ae424d960c26247dd6c32edc70b295c744c43": "DOGE",
        "0x570a5d26f7765ecb712c0924e4de545b89fd43df": "SOL",
        "0x1d2f0da169ceb9fc7b3144628db156f3f6c60dbe": "XRP",
        "0x55d398326f99059ff775485246999027b3197955": "USDT",
        "0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d": "USDC",
    },
    2372: { // BESC Hyperchain
        // Real tokens on BESC
        "0x33e22f85cc1877697773ca5c85988663388883a0": "BESC",  // WBESC
        "0xb54ad626e127f0f228dbeab6f2a61e8e6e029a4b": "USDC",  // BUSDC stable
        "0xf794e0b0c0a91099ccacac296193e04fb860442a": "MONEY", // MONEY token
        // Synthetic index tokens (unique identifiers only — no real ERC-20 on BESC)
        "0x7130d2a12b9bcbfae4f2634d864a1ee1ce3ead9c": "BTC",
        "0x2170ed0880ac9a755fd29b2688956bd959f933f8": "ETH",
        "0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c": "BNB",
        "0x570a5d26f7765ecb712c0924e4de545b89fd43df": "SOL",
        "0x1d2f0da169ceb9fc7b3144628db156f3f6c60dbe": "XRP",
        "0xba2ae424d960c26247dd6c32edc70b295c744c43": "DOGE",
        "0x3ee2200efb3400fabb9aacf31297cbdd1d435d47": "ADA",
        "0x85eac5ac2f758618dfa09bdbe0cf174e7d574d5b": "TRX",
        "0xf8a0bf9cf54bb92f17374d9e9a321e6a111a51bd": "LINK",
        "0x1ce0c2827e2ef14d5c4f29a091d735a204794041": "AVAX",
        "0x2859e4544c4bb03966803b044a93563bd2d0dd4d": "SHIB",
        "0x76a797a59ba2c17726896976b7b3747bfd1d220f": "TON",
        "0x7083609fce4d1d8dc0c979aab8c869ea2c873402": "DOT",
        "0x1fa4a73a3f0133f0025378af00236f3abdee5d63": "NEAR",
        "0xcc42724c6683b7e57334c4e856f4c9965ed682bd": "MATIC",
        "0x4338665cbb7b2485a8855a139b75d5e34ab0db94": "LTC",
        "0xbf5140a22578168fd562dccf235e5d43a02ce9b1": "UNI",
        "0x0eb3a705fc54725037cc9e008bdede697f62f335": "ATOM",
        "0xa050ffb3eeb8200eeb7f61ce34ff644420fd3522": "ARB",
        "0x5c1e3a76aba3c84afbce7a38e8ef9dea3f9adfc8": "OP",
        // Synthetic stock index tokens (placeholder addresses)
        "0x53544f434b000000000000000000000000000001": "AAPL",
        "0x53544f434b000000000000000000000000000002": "TSLA",
        "0x53544f434b000000000000000000000000000003": "NVDA",
        "0x53544f434b000000000000000000000000000004": "MSFT",
        "0x53544f434b000000000000000000000000000005": "GOOGL",
        "0x53544f434b000000000000000000000000000006": "AMZN",
        "0x53544f434b000000000000000000000000000007": "META",
        "0x53544f434b000000000000000000000000000008": "SPY",
        "0x53544f434b000000000000000000000000000009": "QQQ",
        "0x53544f434b00000000000000000000000000000a": "GOLD",
    },
}

// Convert USD float to 1e30 BigInt (8 decimal places of precision, well within
// float64's 15-digit limit for the intermediate integer).
const toPrec30 = (usd: number): bigint =>
    BigInt(Math.round(usd * 1e8)) * (10n ** 22n)

/**
 * Returns live prices as { [tokenAddress]: bigint } in 1e30 USD precision —
 * the same format useInfoTokens expects for setTokenUsingIndexPrices().
 */
export function useLiveIndexPrices(chainId = 56): Record<string, bigint> {
    const prices = useLivePrices()
    const addrMap = ADDRESS_TO_SYMBOL_BY_CHAIN[chainId] ?? ADDRESS_TO_SYMBOL_BY_CHAIN[56]
    const result: Record<string, bigint> = {}
    for (const [addr, sym] of Object.entries(addrMap)) {
        const price = prices[sym]
        if (price !== undefined && price > 0) {
            result[addr] = toPrec30(price)
        }
    }
    return result
}
